import {
  FOLDER_IMPORT_BATCH_MAX_BYTES,
  FOLDER_IMPORT_BATCH_MAX_FILES,
} from './folderImportLimits';

export type FolderImportItem = {
  file: File;
  /** Relative path used by the server (after common-root strip). */
  path: string;
};

function normalizePath(raw: string): string {
  return raw.replace(/\\/g, '/').replace(/^\.?\//, '').replace(/\/+/g, '/').trim();
}

/**
 * Strip the common top-level folder segment (directory picker / drag-drop root)
 * once across the whole import so batches keep consistent relative paths.
 */
export function stripCommonImportRootPaths(paths: string[]): string[] {
  const normalized = paths.map((raw) => normalizePath(raw)).filter(Boolean);
  if (normalized.length === 0) return normalized;
  if (!normalized.some((path) => path.includes('/'))) return normalized;

  const firstSegment = normalized[0].split('/')[0];
  if (!firstSegment || !normalized.every((path) => path.split('/')[0] === firstSegment)) {
    return normalized;
  }

  return normalized.map((path) => {
    const slash = path.indexOf('/');
    if (slash === -1) return '';
    return path.slice(slash + 1);
  });
}

export function buildFolderImportItems(
  files: File[],
  options?: { stripCommonRoot?: boolean },
): FolderImportItem[] {
  const stripCommonRoot = options?.stripCommonRoot !== false;
  const rawPaths = files.map(
    (file) => (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name,
  );
  const relativePaths = stripCommonRoot ? stripCommonImportRootPaths(rawPaths) : rawPaths.map(normalizePath);
  const items: FolderImportItem[] = [];
  for (let i = 0; i < files.length; i++) {
    const path = relativePaths[i] || files[i].name;
    if (!path) continue;
    items.push({ file: files[i], path });
  }
  return items;
}

export type FolderImportBatch = {
  items: FolderImportItem[];
  bytes: number;
};

/**
 * Split import items into batches by file count and total bytes.
 * A single oversized file (still under the per-file limit) becomes its own batch.
 */
export function chunkFolderImportItems(
  items: FolderImportItem[],
  options?: { maxFiles?: number; maxBytes?: number },
): FolderImportBatch[] {
  const maxFiles = options?.maxFiles ?? FOLDER_IMPORT_BATCH_MAX_FILES;
  const maxBytes = options?.maxBytes ?? FOLDER_IMPORT_BATCH_MAX_BYTES;
  const batches: FolderImportBatch[] = [];
  let current: FolderImportItem[] = [];
  let currentBytes = 0;

  const flush = () => {
    if (current.length === 0) return;
    batches.push({ items: current, bytes: currentBytes });
    current = [];
    currentBytes = 0;
  };

  for (const item of items) {
    const size = item.file.size;
    const wouldExceedFiles = current.length >= maxFiles;
    const wouldExceedBytes = current.length > 0 && currentBytes + size > maxBytes;
    if (wouldExceedFiles || wouldExceedBytes) {
      flush();
    }
    current.push(item);
    currentBytes += size;
  }
  flush();
  return batches;
}
